import { useEffect, useState } from "react";
import { supabase, dbToDeal } from "../../supabase";
import { OUTREACH_PROMOTE_STAGE } from "../../constants/dealStages";
import { followUpDays, fmtMoneyShort, fmtShortDate } from "../../services/outreachFlow";

// The LOI on an Outreach deal's page, above Dashboard / Deal Room / IC Memo:
// what we offered and on what terms, who it went through, whether the broker
// confirmed receipt and whether the seller countered, when to chase - and the
// moves that end it: Declined, or into Pipeline. Fields save as you leave them.

const FIELDS = [
  ["offerPrice", "offer_price", "Our offer ($)", "number"],
  ["loiDate", "loi_date", "LOI date", "date"],
  ["loiDeposit", "loi_deposit", "Deposit ($)", "number"],
  ["loiDdDays", "loi_dd_days", "Due diligence (days)", "number"],
  ["loiClosingDays", "loi_closing_days", "Closing (days)", "number"],
  ["followUpDate", "follow_up_date", "Follow up on", "date"],
  ["brokerFirm", "broker_firm", "Brokerage", "text"],
  ["brokerName", "broker_name", "Broker", "text"],
  ["brokerEmail", "primary_broker", "Broker email", "text"],
  ["brokerPhone", "broker_phone", "Broker phone", "text"],
  ["counterPrice", "counter_price", "Seller's counter ($)", "number"],
];

export default function OutreachPanel({ deal, readOnly, onUpdated, onStage }) {
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  useEffect(() => { setDraft({}); }, [deal.id]);

  const value = (k) => (k in draft ? draft[k] : deal[k] ?? "");
  async function commit(key, col, type) {
    if (!(key in draft)) return;
    const raw = draft[key];
    const v = raw === "" ? null : type === "number" ? Number(raw) : raw;
    if ((deal[key] ?? null) === v) return;
    const { data, error: e } = await supabase.from("deals").update({ [col]: v }).eq("id", deal.id).select().single();
    if (e) { setError(e.message); return; }
    setError(null);
    onUpdated?.({ ...dbToDeal(data), photoPath: deal.photoPath });
    setDraft((d) => { const n = { ...d }; delete n[key]; return n; });
  }

  const due = followUpDays(deal);
  const psf = deal.offerPrice && deal.squareFeet ? Math.round(deal.offerPrice / deal.squareFeet) : null;
  const milestone = (done, label, at) => (
    <div className={`flex items-center gap-2 text-xs ${done ? "text-[#f4f4f4]" : "text-[#6b6b6b]"}`}>
      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${done ? "bg-[#4f9d76] text-white" : "border border-[#4a4a4a]"}`}>{done ? "✓" : ""}</span>
      {label}{at ? <span className="text-[#8a8a8a]">· {fmtShortDate(at)}</span> : null}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto w-full px-6 pt-4">
      <div className="bg-[#242424] border border-[rgba(155,107,204,0.35)] rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-baseline gap-3">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#b892e0]">Outreach · LOI</span>
            <span className="text-sm text-[#f4f4f4] font-semibold">{fmtMoneyShort(deal.offerPrice)}{psf ? <span className="text-[#8a8a8a] font-normal"> · ${psf}/SF</span> : null}</span>
            {due != null && (
              <span className={`text-xs ${due < 0 ? "text-[#f04f44] font-semibold" : due === 0 ? "text-[#c8922f]" : "text-[#8a8a8a]"}`}>
                {due < 0 ? `Follow-up ${-due}d overdue` : due === 0 ? "Follow up today" : `Follow up ${fmtShortDate(deal.followUpDate)}`}
              </span>
            )}
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2">
              {!deal.confirmedReceiptAt && deal.stage !== "Declined" && (
                <button onClick={() => onStage("Confirmed Receipt")} className="text-xs px-3 py-1.5 rounded-lg bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4]">Broker confirmed receipt</button>
              )}
              {!deal.counteredAt && deal.stage !== "Declined" && (
                <button onClick={() => onStage("Countered")} className="text-xs px-3 py-1.5 rounded-lg bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4]">Seller countered</button>
              )}
              {deal.stage !== "Declined" && (
                <button onClick={() => onStage("Declined")} className="text-xs px-3 py-1.5 rounded-lg text-[#8a8a8a] hover:text-[#f04f44] hover:bg-[#2c2c2c]">Declined</button>
              )}
              <button onClick={() => onStage("__promote__")} style={{ color: "#ffffff" }}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#4f9d76] hover:bg-[#5fae86]">
                Move to Pipeline ({OUTREACH_PROMOTE_STAGE}) →
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1.5 mb-4">
          {milestone(true, "LOI sent", deal.loiDate)}
          {milestone(!!deal.confirmedReceiptAt, "Broker confirmed receipt", deal.confirmedReceiptAt)}
          {milestone(!!deal.counteredAt, deal.counterPrice ? `Seller countered at ${fmtMoneyShort(deal.counterPrice)}` : "Seller countered", deal.counteredAt)}
          {deal.stage === "Declined" && milestone(true, "Declined", deal.declinedAt)}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {FIELDS.map(([key, col, label, type]) => (
            <label key={key} className="block">
              <span className="block text-[10px] uppercase tracking-wider text-[#8a8a8a] mb-1">{label}</span>
              <input type={type === "number" ? "number" : type} disabled={readOnly}
                value={value(key) ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                onBlur={() => commit(key, col, type)}
                className="w-full bg-[#2c2c2c] border border-[#3a3a3a] focus:border-[#b892e0] focus:outline-none rounded-md px-2 py-1.5 text-xs text-[#f4f4f4] disabled:opacity-70" />
            </label>
          ))}
        </div>
        <label className="block mt-3">
          <span className="block text-[10px] uppercase tracking-wider text-[#8a8a8a] mb-1">Other key terms</span>
          <textarea rows={2} disabled={readOnly} value={value("loiTerms") ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, loiTerms: e.target.value }))}
            onBlur={() => commit("loiTerms", "loi_terms", "text")}
            className="w-full bg-[#2c2c2c] border border-[#3a3a3a] focus:border-[#b892e0] focus:outline-none rounded-md px-2 py-1.5 text-xs text-[#f4f4f4] disabled:opacity-70" />
        </label>
        {error && <p className="text-xs text-[#f04f44] mt-2">{error}</p>}
      </div>
    </div>
  );
}
